(() => {
  if (window.__FAJ_BLOG_INSIGHTS_V6__) return;
  window.__FAJ_BLOG_INSIGHTS_V6__ = true;

  const seen = new WeakSet();
  const requests = new WeakMap();
  const stickyMedia = window.matchMedia('(min-width: 990px)');
  const stickyAncestors = new Map();

  function restoreStickyAncestors(section) {
    const ancestors = stickyAncestors.get(section) || [];
    ancestors.forEach((ancestor) => {
      ancestor.classList.remove('fajblog-sticky-filter-overflow-fix');
    });
    stickyAncestors.delete(section);
  }

  function prepareStickySidebar(section) {
    restoreStickyAncestors(section);

    if (!stickyMedia.matches || !section.querySelector('[data-faj-blog-filter-sticky]')) {
      return;
    }

    const fixed = [];
    let ancestor = section.parentElement;

    while (ancestor && ancestor !== document.body) {
      const styles = window.getComputedStyle(ancestor);
      const overflowValues = [styles.overflow, styles.overflowX, styles.overflowY];
      const clipsSticky = overflowValues.some((value) =>
        ['hidden', 'clip', 'auto', 'scroll'].includes(value)
      );
      const isActualVerticalScroller =
        ancestor.scrollHeight > ancestor.clientHeight + 2 &&
        ['auto', 'scroll'].includes(styles.overflowY);

      if (clipsSticky && !isActualVerticalScroller) {
        ancestor.classList.add('fajblog-sticky-filter-overflow-fix');
        fixed.push(ancestor);
      }

      ancestor = ancestor.parentElement;
    }

    if (fixed.length) stickyAncestors.set(section, fixed);
  }

  function refreshStickySidebars(root = document) {
    const sections = [];
    if (root.matches?.('[data-faj-blog-feed]')) sections.push(root);
    root.querySelectorAll?.('[data-faj-blog-feed]').forEach((section) => sections.push(section));
    sections.forEach(prepareStickySidebar);
  }

  function revealWithin(root = document) {
    const elements = Array.from(
      root.querySelectorAll?.('[data-faj-blog-reveal]') || []
    ).filter((element) => !seen.has(element));

    if (!elements.length) return;

    if (
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      typeof IntersectionObserver === 'undefined'
    ) {
      elements.forEach((element) => {
        seen.add(element);
        element.classList.add('is-visible');
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          seen.add(entry.target);
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.08
      }
    );

    elements.forEach((element) => observer.observe(element));
  }

  function cleanPublicUrl(input) {
    const url = new URL(input, window.location.origin);
    url.searchParams.delete('section_id');
    return url;
  }

  function canAjaxLink(event, link) {
    if (!link) return false;
    if (event.defaultPrevented) return false;
    if (event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (link.target && link.target !== '_self') return false;

    const url = cleanPublicUrl(link.href);
    return url.origin === window.location.origin;
  }

  function setLoading(section, loading) {
    section.classList.toggle('is-loading', loading);
    section.setAttribute('aria-busy', loading ? 'true' : 'false');
  }

  function findRemoteFeed(doc, section) {
    const sectionId = section.dataset.sectionId;

    if (sectionId && window.CSS?.escape) {
      const exact = doc.querySelector(
        `[data-faj-blog-feed][data-section-id="${CSS.escape(sectionId)}"]`
      );
      if (exact) return exact;
    }

    return doc.querySelector('[data-faj-blog-feed]');
  }

  function replaceFeedParts(section, remoteSection) {
    const currentTopics = section.querySelector('[data-faj-blog-topics]');
    const remoteTopics = remoteSection.querySelector('[data-faj-blog-topics]');
    const currentResults = section.querySelector('[data-faj-blog-results]');
    const remoteResults = remoteSection.querySelector('[data-faj-blog-results]');

    if (!currentResults || !remoteResults) {
      throw new Error('FAJ blog results region was not found.');
    }

    if (currentTopics && remoteTopics) {
      currentTopics.replaceWith(remoteTopics);
    }

    currentResults.replaceWith(remoteResults);
  }

  async function loadFeed(section, inputUrl, options = {}) {
    if (!section) return;

    const {
      pushState = true,
      preserveScroll = true
    } = options;

    const previousController = requests.get(section);
    previousController?.abort();

    const controller = new AbortController();
    requests.set(section, controller);

    const publicUrl = cleanPublicUrl(inputUrl);
    const savedScrollY = window.scrollY;
    const savedScrollX = window.scrollX;

    setLoading(section, true);

    try {
      const response = await fetch(publicUrl.toString(), {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'text/html'
        },
        signal: controller.signal,
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`FAJ blog filter request failed: ${response.status}`);
      }

      const html = await response.text();
      const remoteDocument = new DOMParser().parseFromString(html, 'text/html');
      const remoteSection = findRemoteFeed(remoteDocument, section);

      if (!remoteSection) {
        throw new Error('FAJ blog feed was not found in the filtered Shopify page.');
      }

      replaceFeedParts(section, remoteSection);

      if (pushState) {
        window.history.pushState(
          { fajBlogFilter: true },
          '',
          publicUrl.toString()
        );
      }

      revealWithin(section);

      window.requestAnimationFrame(() => {
        if (preserveScroll) {
          window.scrollTo({
            top: savedScrollY,
            left: savedScrollX,
            behavior: 'auto'
          });
        }
      });
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error('FAJ blog filtering fallback:', error);
      window.location.assign(publicUrl.toString());
    } finally {
      if (requests.get(section) === controller) {
        requests.delete(section);
        setLoading(section, false);
      }
    }
  }

  function onDocumentClick(event) {
    const link = event.target.closest(
      '[data-faj-blog-filter], [data-faj-blog-page-link]'
    );

    if (!canAjaxLink(event, link)) return;

    const section = link.closest('[data-faj-blog-feed]');
    if (!section) return;

    event.preventDefault();

    loadFeed(section, link.href, {
      pushState: true,
      preserveScroll: true
    });
  }

  function onPopState() {
    document.querySelectorAll('[data-faj-blog-feed]').forEach((section) => {
      loadFeed(section, window.location.href, {
        pushState: false,
        preserveScroll: true
      });
    });
  }

  function init(root = document) {
    revealWithin(root);
    refreshStickySidebars(root);
  }

  const onStickyViewportChange = () => {
    document.querySelectorAll('[data-faj-blog-feed]').forEach((section) => {
      prepareStickySidebar(section);
    });
  };

  if (typeof stickyMedia.addEventListener === 'function') {
    stickyMedia.addEventListener('change', onStickyViewportChange);
  } else if (typeof stickyMedia.addListener === 'function') {
    stickyMedia.addListener(onStickyViewportChange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => init(document),
      { once: true }
    );
  } else {
    init(document);
  }

  document.addEventListener('click', onDocumentClick);
  window.addEventListener('popstate', onPopState);

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.('[data-faj-blog-feed]')) {
      restoreStickyAncestors(event.target);
    }
    event.target.querySelectorAll?.('[data-faj-blog-feed]').forEach(restoreStickyAncestors);
  });
})();
